import { useEffect, useLayoutEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SessionUser } from '@simsm/shared-types';
import { apiClient, apiErrorKey } from '../app/api-client';
import {
  activeRegistrarOf,
  announceSession,
  applySession,
  followSession,
} from '../app/active-registrar';
import { useSession } from './use-session';

export interface RegistrarOption {
  idno: string;
  name: string;
}

export interface ActiveRegistrar {
  /** The Registrar the session acts for. Empty for a role that holds none, or before a choice. */
  idno: string;
  /** Its display name, falling back to the IDNO the catalog could not resolve. */
  name: string;
  /** Every Registrar the session grants, in the order the assertion named them. */
  options: RegistrarOption[];
  /** Whether there is anything to choose. False is the common case and draws no control. */
  hasChoice: boolean;
  /**
   * There is a choice and none is made: nothing may load for a Registrar until
   * the person picks one (register UC11-05, CF-27).
   */
  awaitingChoice: boolean;
  /** Asks the server to make `idno` the active Registrar of the session. */
  choose: (idno: string) => void;
  /** The Registrar being selected while the server is asked, otherwise null. */
  choosing: string | null;
  /** The translation key of why the last selection failed, otherwise null. */
  chooseErrorKey: string | null;
}

/**
 * The Registrar the session works on (owner decision CF-27, 2026-10-13,
 * docs/shape-ops-tenant-selector.md §9). The server session holds it and the
 * menu's selection is the only thing that changes it, so `choose` asks the
 * server, and the answer is the session every tab then follows.
 *
 * `registrarNames` is index-aligned with `registrarScope` (owner decision D5),
 * and an IDNO the catalog does not know keeps its place as an empty string, so
 * the fallback is that IDNO and never a blank: "I do not know this Registrar"
 * and "this Registrar is withdrawn" are different sentences and the reader needs
 * the second one. Same discipline as SidebarAccount.tsx.
 */
export function useActiveRegistrar(): ActiveRegistrar {
  const session = useSession().data;
  const queryClient = useQueryClient();
  const idno = session ? activeRegistrarOf(session) : '';

  // Before paint: a session read that acts for another Registrar than the one
  // the caches were answered for (a role switch, a selection made where this tab
  // did not hear it) empties them and remounts the workspace before anything is
  // drawn under the new name.
  useLayoutEffect(() => {
    if (session !== undefined) followSession(queryClient, session);
  }, [session, queryClient]);

  const selection = useMutation({
    mutationFn: async (next: string): Promise<SessionUser> => {
      const { data } = await apiClient.patch<SessionUser>('/auth/active-registrar', {
        activeRegistrar: next,
      });
      return data;
    },
    onSuccess: (answered) => {
      applySession(queryClient, answered);
      announceSession(answered);
    },
  });

  // A refusal describes the Registrar that was in force when it was given.
  const { reset } = selection;
  useEffect(() => {
    reset();
  }, [idno, reset]);

  const names = session?.registrarNames ?? [];
  const options: RegistrarOption[] = (session?.registrarScope ?? []).map((entry, index) => ({
    idno: entry,
    name: names[index] || entry,
  }));

  return {
    idno,
    name: options.find((option) => option.idno === idno)?.name ?? idno,
    options,
    hasChoice: options.length > 1,
    awaitingChoice: options.length > 1 && !idno,
    choose: (next: string): void => {
      if (next === idno || selection.isPending) return;
      selection.mutate(next);
    },
    choosing: selection.isPending ? selection.variables : null,
    chooseErrorKey: selection.isError
      ? apiErrorKey(selection.error, 'registrar.choose.failed')
      : null,
  };
}
